import { MedplumClient, normalizeErrorString, resolveId } from "@medplum/core";
import type {
  Invoice,
  Organization,
  Patient,
  PaymentReconciliation,
  Resource,
} from "@medplum/fhirtypes";

import chunk from "lodash/chunk";
import uniqBy from "lodash/uniqBy";

import { LEDGER_IDENTIFIER_CHUNK_SIZE } from "@/billing/constants/ledger-search";
import {
  ARCHIVED_STATEMENT_TAG_QUERY,
  DELETED_STATEMENT_TAG_QUERY,
} from "@/billing/constants/statements";
import {
  DEFAULT_PRACTICE_LEDGER_SORT,
  type ExportPracticeLedgerCsvResult,
  type LoadPracticeLedgerResult,
  PRACTICE_LEDGER_PAGE_SIZE,
  type PracticeLedgerDateRange,
  type PracticeLedgerSearch,
  type PracticeLedgerSortState,
} from "@/billing/types/practice-ledger";
import { statementNumberOf } from "@/billing/utils/invoice-statement-number";
import {
  getInvoicePayerOrganizationId,
  getPaymentIssuerOrganizationId,
} from "@/billing/utils/ledger-source";
import {
  buildPracticeLedgerCsv,
  getPracticeLedgerExportFilename,
} from "@/billing/utils/practice-ledger-csv";
import { formatUtcCalendarDate } from "@/billing/utils/practice-ledger-date-range";
import {
  filterPracticeLedgerRows,
  flattenPracticeLedgerRows,
  paginatePracticeLedgerRows,
  sortPracticeLedgerRows,
  sumPracticeLedgerTotals,
} from "@/billing/utils/practice-ledger-flatten";
import { QuiltedHealthIdentifierSystems } from "@/fhir/enums/quilted-health";
import { processWithConcurrencyLimit } from "@/utils/concurrency";
import { identifierValues, joinReferenceString } from "@/utils/medplum";
import { fetchAllPaginatedResources } from "@/utils/pagination";

const LEDGER_INVOICE_STATUSES = "issued,balanced";

const BILLING_IDENTIFIER_QUERY = `${QuiltedHealthIdentifierSystems.BillingIdentifier}|`;

/**
 * Concurrent identifier and `_id` chunk searches (invoices, patients, payer organizations).
 * Caps fan-out on a practice-wide window so a large ledger cannot open unbounded parallel requests.
 */
const LEDGER_RESOURCE_SEARCH_CONCURRENCY = 10;

export interface LoadPracticeLedgerOptions {
  search?: PracticeLedgerSearch;
  page?: number;
  pageSize?: number;
  sort?: PracticeLedgerSortState;
  /** When false, `rows` is the full searched window (CSV export). Defaults to true. */
  paginate?: boolean;
}

/**
 * Loads the practice Ledger for one Show: window.
 *
 * Invoices are searched by compartment, issuer, and `Invoice.date` (`date=ge` plus `_filter`
 * `date le`). PaymentReconciliations are searched by compartment and status `active`; the Show:
 * window is applied to `paymentDate` when flattening, because that field is not a FHIR search
 * parameter. Payments whose statement falls outside the window still appear: their Invoices are
 * loaded by `invoice-identifier` so the row can show client, MRN, #ID, and source. KPIs sum
 * charges plus payments and payment reversals in the window; search and paging only change the table.
 */
export async function loadPracticeLedger(
  medplum: MedplumClient,
  practiceId: string,
  dateRange: PracticeLedgerDateRange,
  options: LoadPracticeLedgerOptions = {},
): Promise<LoadPracticeLedgerResult> {
  if (!practiceId.trim()) {
    return { ok: false, reason: "missing_practice_id" };
  }

  const page = options.page ?? 1;
  const pageSize = options.pageSize ?? PRACTICE_LEDGER_PAGE_SIZE;
  const sort = options.sort ?? DEFAULT_PRACTICE_LEDGER_SORT;
  const compartment = joinReferenceString("Organization", practiceId);

  try {
    const [windowInvoices, paymentReconciliations] = await Promise.all([
      readWindowInvoices(medplum, practiceId, dateRange, compartment),
      readWindowPayments(medplum, compartment),
    ]);
    const invoicesByStatementNumber = await invoicesForPayments(
      medplum,
      practiceId,
      compartment,
      windowInvoices,
      paymentReconciliations,
    );
    const invoices = [...invoicesByStatementNumber.values()];
    const [patientsById, organizations] = await Promise.all([
      readPatients(medplum, compartment, clientIdsOf(invoices)),
      fetchPayerOrganizations(medplum, invoices, paymentReconciliations),
    ]);

    const rows = flattenPracticeLedgerRows({
      windowInvoices,
      paymentReconciliations,
      invoicesByStatementNumber,
      patientsById,
      organizationsById: organizationsByIdFrom(organizations),
      dateRange,
    });
    const totals = sumPracticeLedgerTotals(rows);
    const searchedRows = sortPracticeLedgerRows(
      filterPracticeLedgerRows(rows, options.search),
      sort,
    );
    const pageRows =
      options.paginate === false
        ? searchedRows
        : paginatePracticeLedgerRows(searchedRows, page, pageSize);

    return {
      ok: true,
      data: {
        rows: pageRows,
        totalRows: searchedRows.length,
        totals,
      },
    };
  } catch (error) {
    return { ok: false, reason: "load_failed", details: normalizeErrorString(error) };
  }
}

/**
 * Builds a CSV of the Show: window after search and sort, with no page slice.
 */
export async function exportPracticeLedgerCsv(
  medplum: MedplumClient,
  practiceId: string,
  dateRange: PracticeLedgerDateRange,
  options: Pick<LoadPracticeLedgerOptions, "search" | "sort"> = {},
): Promise<ExportPracticeLedgerCsvResult> {
  const result = await loadPracticeLedger(medplum, practiceId, dateRange, {
    search: options.search,
    sort: options.sort,
    paginate: false,
  });
  if (!result.ok) return result;

  return {
    ok: true,
    data: {
      csvContent: buildPracticeLedgerCsv(result.data.rows),
      filename: getPracticeLedgerExportFilename(dateRange),
      rowCount: result.data.rows.length,
    },
  };
}

async function readWindowInvoices(
  medplum: MedplumClient,
  practiceId: string,
  dateRange: PracticeLedgerDateRange,
  compartment: string,
): Promise<Invoice[]> {
  return requireAllPages<Invoice>(medplum, "Invoice", {
    _compartment: compartment,
    issuer: joinReferenceString("Organization", practiceId),
    identifier: BILLING_IDENTIFIER_QUERY,
    status: LEDGER_INVOICE_STATUSES,
    "_tag:not": `${ARCHIVED_STATEMENT_TAG_QUERY},${DELETED_STATEMENT_TAG_QUERY}`,
    ...invoiceDateSearchParams(dateRange),
  });
}

async function readWindowPayments(
  medplum: MedplumClient,
  compartment: string,
): Promise<PaymentReconciliation[]> {
  return requireAllPages<PaymentReconciliation>(medplum, "PaymentReconciliation", {
    _compartment: compartment,
    status: "active",
  });
}

/**
 * Window invoices plus any statement Invoices that payments in the window reference but that were
 * not themselves dated in the window.
 */
async function invoicesForPayments(
  medplum: MedplumClient,
  practiceId: string,
  compartment: string,
  windowInvoices: Invoice[],
  paymentReconciliations: PaymentReconciliation[],
): Promise<Map<string, Invoice>> {
  const invoicesByStatementNumber = new Map<string, Invoice>();
  for (const invoice of windowInvoices) {
    const statementNumber = statementNumberOf(invoice);
    if (statementNumber) invoicesByStatementNumber.set(statementNumber, invoice);
  }

  const missingStatementNumbers = uniqueStatementNumbers(paymentReconciliations).filter(
    (statementNumber) => !invoicesByStatementNumber.has(statementNumber),
  );
  if (missingStatementNumbers.length === 0) return invoicesByStatementNumber;

  const extraInvoices = uniqBy(
    (
      await processWithConcurrencyLimit(
        chunk(missingStatementNumbers, LEDGER_IDENTIFIER_CHUNK_SIZE),
        (statementNumberChunk) =>
          requireAllPages<Invoice>(medplum, "Invoice", {
            _compartment: compartment,
            issuer: joinReferenceString("Organization", practiceId),
            identifier: invoiceIdentifierQuery(statementNumberChunk),
            status: LEDGER_INVOICE_STATUSES,
            "_tag:not": `${ARCHIVED_STATEMENT_TAG_QUERY},${DELETED_STATEMENT_TAG_QUERY}`,
          }),
        LEDGER_RESOURCE_SEARCH_CONCURRENCY,
      )
    ).flat(),
    "id",
  );

  for (const invoice of extraInvoices) {
    const statementNumber = statementNumberOf(invoice);
    if (statementNumber) invoicesByStatementNumber.set(statementNumber, invoice);
  }

  return invoicesByStatementNumber;
}

async function readPatients(
  medplum: MedplumClient,
  compartment: string,
  clientIds: string[],
): Promise<Map<string, Patient>> {
  const ids = [...new Set(clientIds)];
  if (ids.length === 0) return new Map();

  const pages = await processWithConcurrencyLimit(
    chunk(ids, LEDGER_IDENTIFIER_CHUNK_SIZE),
    async (idChunk) => {
      try {
        return await requireAllPages<Patient>(medplum, "Patient", {
          _compartment: compartment,
          _id: idChunk.join(","),
        });
      } catch {
        return [];
      }
    },
    LEDGER_RESOURCE_SEARCH_CONCURRENCY,
  );

  const patientsById = new Map<string, Patient>();
  for (const patient of pages.flat()) {
    if (patient.id) patientsById.set(patient.id, patient);
  }
  return patientsById;
}

async function fetchPayerOrganizations(
  medplum: MedplumClient,
  invoices: Invoice[],
  paymentReconciliations: PaymentReconciliation[],
): Promise<Organization[]> {
  const organizationIds = uniquePayerOrganizationIds(invoices, paymentReconciliations);
  if (organizationIds.length === 0) return [];

  const pages = await processWithConcurrencyLimit(
    chunk(organizationIds, LEDGER_IDENTIFIER_CHUNK_SIZE),
    (organizationIdChunk) =>
      requireAllPages<Organization>(medplum, "Organization", {
        _id: organizationIdChunk.join(","),
      }),
    LEDGER_RESOURCE_SEARCH_CONCURRENCY,
  );

  return uniqBy(pages.flat(), "id");
}

async function requireAllPages<T extends Resource>(
  medplum: MedplumClient,
  resourceType: T["resourceType"],
  searchParams: Record<string, string>,
): Promise<T[]> {
  const result = await fetchAllPaginatedResources<T>(medplum, resourceType, searchParams, {
    throwOnPageError: true,
  });
  if (!result.success) throw result.error;
  return result.data;
}

function invoiceDateSearchParams(dateRange: PracticeLedgerDateRange): {
  date: string;
  _filter: string;
} {
  return {
    date: `ge${formatUtcCalendarDate(dateRange.startDate)}`,
    _filter: `date le ${formatUtcCalendarDate(dateRange.endDate)}`,
  };
}

function uniqueStatementNumbers(paymentReconciliations: PaymentReconciliation[]): string[] {
  const values = paymentReconciliations.flatMap((paymentReconciliation) =>
    identifierValues(paymentReconciliation, QuiltedHealthIdentifierSystems.InvoiceIdentifier),
  );
  return [...new Set(values)];
}

function invoiceIdentifierQuery(statementNumbers: string[]): string {
  return statementNumbers
    .map(
      (statementNumber) => `${QuiltedHealthIdentifierSystems.InvoiceIdentifier}|${statementNumber}`,
    )
    .join(",");
}

function uniquePayerOrganizationIds(
  invoices: Invoice[],
  paymentReconciliations: PaymentReconciliation[],
): string[] {
  const ids = new Set<string>();
  for (const invoice of invoices) {
    const organizationId = getInvoicePayerOrganizationId(invoice);
    if (organizationId) ids.add(organizationId);
  }
  for (const paymentReconciliation of paymentReconciliations) {
    const organizationId = getPaymentIssuerOrganizationId(paymentReconciliation);
    if (organizationId) ids.add(organizationId);
  }
  return [...ids];
}

function clientIdsOf(invoices: Invoice[]): string[] {
  return invoices.reduce<string[]>((ids, invoice) => {
    const clientId = resolveId(invoice.subject);
    if (clientId) ids.push(clientId);
    return ids;
  }, []);
}

function organizationsByIdFrom(organizations: Organization[]): Map<string, Organization> {
  const byId = new Map<string, Organization>();
  for (const organization of organizations) {
    if (organization.id) byId.set(organization.id, organization);
  }
  return byId;
}
